import type { AppState } from '../manager';
import type { Player } from '../types';
import { mergeAFSDV01History } from './afsDv01History';
import { mergeValuation } from './valuation';

export const currentPlayerData = (state: AppState, playerId: number) =>
  state.players.items[playerId] ?? state.players.pending_updates?.[playerId];

const mergeHistory = <T>(
  older: T[] | undefined,
  newer: T[],
  key: (item: T) => string | number,
) =>
  Array.from(
    new Map(
      [...(older ?? []), ...newer].map(item => [key(item), item]),
    ).values(),
  );

const mergeEarlyUpdates = (base: Partial<Player>, updates: Partial<Player>) => {
  const merged = { ...base, ...mergeValuation(base, updates) };
  if (updates.metrics) {
    merged.metrics = mergeHistory(base.metrics, updates.metrics, row => row.id);
  }
  if (updates.player_history) {
    merged.player_history = mergeHistory(
      base.player_history,
      updates.player_history,
      row => row.timestamp,
    );
  }
  if (updates.display_series) {
    merged.display_series = mergeHistory(
      base.display_series,
      updates.display_series,
      row => row.display_date,
    );
  }
  if (updates.afs_dv01_history) {
    merged.afs_dv01_history = mergeAFSDV01History(
      base.afs_dv01_history,
      updates.afs_dv01_history,
    );
  }
  return merged;
};

/** Retain early responses without exposing incomplete players to the UI. */
export const updateOrQueuePlayer = (
  state: AppState,
  playerId: number,
  update: Partial<Player>,
) => {
  const player = state.players.items[playerId];
  if (player?.id !== undefined) {
    Object.assign(player, mergeValuation(player, update));
    return;
  }
  const pending = (state.players.pending_updates ??= {});
  pending[playerId] = mergeEarlyUpdates(pending[playerId] ?? {}, update);
};

/** Full roster responses supply identity; queued detail/live data wins once. */
export const hydratePlayer = (state: AppState, player: Player) => {
  const pending = state.players.pending_updates?.[player.id];
  const hydrated = {
    ...state.players.items[player.id],
    ...mergeValuation(state.players.items[player.id], player),
  };
  state.players.items[player.id] = pending
    ? { ...hydrated, ...mergeEarlyUpdates(hydrated, pending) }
    : hydrated;
  if (pending) delete state.players.pending_updates![player.id];
};
